import { shouldReloadForChunkLoad } from "./should-reload-for-chunk-load";

const RELOAD_FLAG = "ms.chunk-load-reloaded";

// Fired when a lazy chunk fails to load: compare the running revision with the
// deployed one and, if the tab is simply out of date, reload it once. The user
// otherwise sits on the error page for a deploy that already fixed itself.
// Returns whether a reload was triggered; the caller still reports to Sentry so
// the failures that are *not* stale bundles stay visible.
export async function recoverFromChunkLoadError(): Promise<boolean> {
  const remoteRevision = await fetchDeployedRevision();
  const shouldReload = shouldReloadForChunkLoad({
    remoteRevision,
    localRevision: import.meta.env.VITE_AWS_COMMIT_ID,
    hasAlreadyReloaded: readReloadFlag() === remoteRevision,
  });
  if (!shouldReload || remoteRevision === null) {
    return false;
  }
  // Keyed on the revision so a later deploy can still recover this tab.
  if (!writeReloadFlag(remoteRevision)) {
    return false;
  }
  window.location.reload();
  return true;
}

// Storage access throws in private/blocked contexts (Safari, hardened
// browsers). The read then reports no flag; the write gate is what fails
// closed — without a persisted flag the loop guard cannot be upheld, so we skip
// the reload rather than risk a loop we cannot stop.
function readReloadFlag(): string | null {
  try {
    return sessionStorage.getItem(RELOAD_FLAG);
  } catch {
    return null;
  }
}

function writeReloadFlag(revision: string): boolean {
  try {
    sessionStorage.setItem(RELOAD_FLAG, revision);
    return true;
  } catch {
    return false;
  }
}

// `BASE_URL` is the deployed base (e.g. `/dist-lcm/`), which is where
// revision.txt sits — deriving it from the entrypoint would need plumbing that
// this call site does not have.
async function fetchDeployedRevision(): Promise<string | null> {
  try {
    const response = await fetch(
      new URL(
        "revision.txt",
        new URL(import.meta.env.BASE_URL, window.location.href),
      ),
      { cache: "no-store" },
    );
    if (!response.ok) {
      return null;
    }
    return (await response.text()).trim();
  } catch {
    return null;
  }
}
